import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import DataTable from "../../components/DataTable";
import TdLink from "../../components/TdLink";
import ConfirmationModal from "../../components/ConfirmationModal";
import { FormCard, FormGrid } from "../../components/FormCard";
import { InputRow, SelectRow, TextAreaRow, DateOnlyRow } from "../../components/Rows";
import { useAuth } from "../../providers/AuthContext";
import { createAppPushMessage, listAppPushMessages } from "../../api/AppPush";

const localDayIso = (dateOnly, endOfDay) => {
    const [year, month, day] = dateOnly.split("-").map(Number);
    if (!year || !month || !day) {
        return undefined;
    }

    return new Date(
        year,
        month - 1,
        day,
        endOfDay ? 23 : 0,
        endOfDay ? 59 : 0,
        endOfDay ? 59 : 0
    ).toISOString();
};

const AppPushPage = () => {
    const { t } = useTranslation();
    const { getAccessToken } = useAuth();
    const [rows, setRows] = useState([]);
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [targetType, setTargetType] = useState("ALL");
    const [sending, setSending] = useState(false);
    const [invalidFields, setInvalidFields] = useState([]);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const loadRequest = useRef(0);

    const load = async () => {
        const request = ++loadRequest.current;
        try {
            const token = await getAccessToken();
            const data = await listAppPushMessages(
                token,
                from ? localDayIso(from, false) : undefined,
                to ? localDayIso(to, true) : undefined
            );
            if (request !== loadRequest.current) {
                return;
            }
            setRows(Array.isArray(data) ? data : []);
        } catch (err) {
            if (request !== loadRequest.current) {
                return;
            }
            toast.error(err?.message || t("toast.failedFetch", { entity: t("nav.appPush") }));
            setRows([]);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [from, to]);

    const requestSend = () => {
        const missing = [];
        if (!title.trim()) {
            missing.push("title");
        }
        if (!body.trim()) {
            missing.push("body");
        }

        if (missing.length > 0) {
            setInvalidFields(missing);
            const labels = missing.map((field) => t(`appPush.${field}`));
            toast.error(t("common.pleaseFillRequiredFields", { fields: labels.join(", ") }));
            return;
        }

        setInvalidFields([]);
        setConfirmOpen(true);
    };

    const handleSend = async () => {
        setConfirmOpen(false);
        setSending(true);
        const toastId = toast.loading(t("appPush.sending"));
        try {
            const token = await getAccessToken();
            await createAppPushMessage(
                { title: title.trim(), body: body.trim(), targetType },
                token
            );
            toast.success(t("appPush.queued"), { id: toastId });
            setTitle("");
            setBody("");
            await load();
        } catch (err) {
            toast.error(err?.message || t("appPush.sendFailed"), { id: toastId });
        } finally {
            setSending(false);
        }
    };

    const columns = [
        {
            key: "createdDateTimeUtc",
            header: t("appPush.sentAt"),
            sortable: true,
            rawCell: true,
            getSortValue: (row) => row.createdDateTimeUtc || "",
            render: (row) => (
                <TdLink to={`/app-push/${row.id}`}>
                    {row.createdDateTimeUtc ? new Date(row.createdDateTimeUtc).toLocaleString() : ""}
                </TdLink>
            ),
        },
        {
            key: "title",
            header: t("appPush.title"),
            sortable: true,
            rawCell: true,
            getSortValue: (row) => row.title || "",
            render: (row) => <TdLink to={`/app-push/${row.id}`}>{row.title || ""}</TdLink>,
        },
        {
            key: "body",
            header: t("appPush.body"),
            sortable: true,
            getSortValue: (row) => row.body || "",
            widthClassName: "min-w-[200px] max-w-[420px] text-left align-top whitespace-pre-wrap break-words",
            render: (row) => row.body || "—",
        },
        {
            key: "targetType",
            header: t("appPush.target"),
            sortable: true,
            getSortValue: (row) => row.targetType || "",
            render: (row) => t(`appPush.targets.${row.targetType}`, { defaultValue: row.targetType }),
        },
        {
            key: "status",
            header: t("common.status"),
            sortable: true,
            getSortValue: (row) => row.status || "",
            render: (row) => row.status,
        },
        {
            key: "createdByName",
            header: t("appPush.sender"),
            sortable: true,
            getSortValue: (row) => row.createdByName || "",
            render: (row) => row.createdByName || "—",
        },
        {
            key: "recipientCount",
            header: t("appPush.recipients"),
            sortable: true,
            getSortValue: (row) => row.recipientCount ?? 0,
            render: (row) => row.recipientCount ?? 0,
        },
    ];

    return (
        <>
            <HeaderBar />
            <div className="flex flex-row w-full">
                <Sidebar />
                <main className="page">
                    <div className="page-content space-y-6">
                        <h2>{t("nav.appPush")}</h2>

                        <FormCard title={t("appPush.new")}>
                            <FormGrid>
                                <InputRow
                                    label={t("appPush.title")}
                                    value={title}
                                    required
                                    invalid={invalidFields.includes("title")}
                                    onChange={setTitle}
                                />
                                <SelectRow
                                    label={t("appPush.target")}
                                    value={targetType}
                                    options={[
                                        { value: "ALL", label: t("appPush.targets.ALL") },
                                        { value: "GITO_CARD", label: t("appPush.targets.GITO_CARD") },
                                    ]}
                                    onChange={setTargetType}
                                />
                                <TextAreaRow
                                    label={t("appPush.body")}
                                    value={body}
                                    rows={5}
                                    maxLength={2000}
                                    invalid={invalidFields.includes("body")}
                                    onChange={setBody}
                                />
                            </FormGrid>
                            <div className="flex justify-end pt-4">
                                <button
                                    className="btn btn-primary"
                                    disabled={sending}
                                    onClick={requestSend}
                                    type="button">
                                    {t("appPush.send")}
                                </button>
                            </div>
                        </FormCard>

                        <FormCard title={t("appPush.history")}>
                            <FormGrid className="mb-4">
                                <DateOnlyRow
                                    label={t("appPush.from")}
                                    value={from}
                                    onChange={setFrom}
                                />
                                <DateOnlyRow
                                    label={t("appPush.to")}
                                    value={to}
                                    onChange={setTo}
                                />
                            </FormGrid>
                            <DataTable
                                rows={rows}
                                columns={columns}
                                rowKey={(row, index) => row.id || index}
                                emptyColSpan={7}
                                emptyContent={t("appPush.empty")}
                            />
                        </FormCard>
                    </div>
                </main>
            </div>
            <ConfirmationModal
                isOpen={confirmOpen}
                title={t("appPush.confirmTitle", {
                    title: title.trim(),
                    target: t(`appPush.targets.${targetType}`, { defaultValue: targetType }),
                })}
                description={t("appPush.confirmBody")}
                okText={t("appPush.send")}
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleSend}
            />
        </>
    );
};

export default AppPushPage;
